import moment from 'moment';
import { TTimelineModel } from './timeline.types';
import { TDate, TLlBoardResponseView } from '@monorepo/types';
import { groupBy } from 'lodash';

const isWeekend = (date: moment.Moment) => {
  const dayOfWeek = date.day();
  const isWeekend = dayOfWeek === 6 || dayOfWeek === 0; // 6 = Saturday, 0 = Sunday
  return isWeekend;
};

export const getTimeline = (date: TDate, boardView: TLlBoardResponseView): TTimelineModel['timeline'] => {
  const getRange = (startDate: moment.Moment, endDate: moment.Moment, increment: 'd' | 'days' = 'd') => {
    return Array.from({ length: endDate.diff(startDate, increment) + 1 }, (_, i) =>
      getCalenderCell(startDate.clone().add(i, increment)),
    );
  };

  const getMonthlyRange = (startDate: moment.Moment, endDate: moment.Moment) => {
    const availableDates = [5, 12, 19, 26];
    return getRange(startDate.clone().day(1), endDate, 'days').filter((cell) =>
      availableDates.includes(parseInt(moment(cell.date).format('DD'))),
    );
  };

  const { dateRange, boardViewType } = boardView;
  const [defaultStart, defaultEnd] = [moment(date).subtract(5, 'd'), moment(date).add(20, 'd')];
  const [start, end] = dateRange ? [moment(dateRange.from), moment(dateRange.to)] : [defaultStart, defaultEnd];

  switch (boardViewType) {
    case 'DAILY':
    case 'WEEKLY':
      return getRange(start, end);

    case 'MONTHLY':
      const monthlyStart = dateRange?.from ? moment(dateRange.from) : moment(date).subtract(45, 'd');
      const monthlyEnd = dateRange?.to ? moment(dateRange.to) : moment(date).add(100, 'd');
      return getMonthlyRange(monthlyStart, monthlyEnd);

    case 'CUSTOM_MONTHLY':
      return getMonthlyRange(start, end);

    default:
      return [];
  }
};

export const getMonths = (timeline: TTimelineModel['timeline']): TTimelineModel['months'] => {
  const months = groupBy(timeline, (v) => v.month);
  return Object.entries(months).map(([_, items]) => {
    const start = moment(items[0].date);
    const end = moment(items[items.length - 1].date);
    const diff = end.diff(start);
    const midTime = start.add(diff / 2);
    return {
      start: start.format('YYYY-MM-DD') as TDate,
      end: end.format('YYYY-MM-DD') as TDate,
      mid: midTime.format('YYYY-MM-DD') as TDate,
    };
  });
};

const getCalenderCell = (updateDate: moment.Moment, pivotDate?: moment.Moment): TTimelineModel['timeline'][number] => {
  const endOfMonth = moment(updateDate.format('YYYY-MM-DD')).endOf('month').format('YYYY-MM-DD') as TDate;
  const date = updateDate.format('YYYY-MM-DD') as TDate;
  return {
    type: isWeekend(updateDate) ? 'HOLIDAY' : 'WORKING_DAY',
    date,
    day: Number(updateDate.format('DD')),
    month: updateDate.format('MMM'),
    dateName: updateDate.format('dddd'),
    dayLetter: updateDate
      .format('dddd')
      .split('')
      .filter((v, i) => i < 3)
      .join(''),
    isPivotMonth: pivotDate
      ? pivotDate.isSame(updateDate, 'month')
      : moment(moment().format('YYYY-MM-DD')).isSame(updateDate, 'month'),
    isPivotDate: moment(moment().format('YYYY-MM-DD')).isSame(updateDate),
    isMonthEnd: endOfMonth === date,
  };
};

export const TIMELINE_TASKS = {
  updatePivot: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    const pivotDate = moment(moment(cm.pivot).format('YYYY-MM-DD')).clone();
    const startOfMonth = pivotDate.clone().startOf('month');
    const lastDayOfLastMonth = startOfMonth.clone().subtract(1, 'day').endOf('month').day() + 1; // Day are calulated from 0 to 6
    const start = lastDayOfLastMonth <= 6 ? startOfMonth.clone().subtract(lastDayOfLastMonth, 'day') : startOfMonth;
    const updatedCalander: TTimelineModel<T1, T2, T3>['timeline'] = cm.timeline.map((v, index) => {
      const updateDate = start.clone().add(index, 'day');
      return {
        ...v,
        ...getCalenderCell(updateDate, pivotDate),
      };
    });

    return {
      ...cm,
      timeline: [...updatedCalander],
    };
  },
  updateMonths: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    const months = groupBy(cm.timeline, (v) => v.month);
    const updatedMonths = Object.entries(months)
      .filter(([_, items]) => items.length >= 3) // Filter out months is there is not enough space
      .map(([_, items]) => {
        const start = moment(items[0].date);
        const end = moment(items[items.length - 1].date);
        const diff = end.diff(start);
        const midTime = start.add(diff / 2);
        return {
          start: start.format('YYYY-MM-DD') as TDate,
          end: end.format('YYYY-MM-DD') as TDate,
          mid: midTime.format('YYYY-MM-DD') as TDate,
        };
      });
    return {
      ...cm,
      months: updatedMonths,
    };
  },
  updateTimelineByPivot: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    return {
      ...cm,
      timeline: getTimeline(cm.pivot, cm.boardView),
    };
  },
  udpateEndOfMonths: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    const months = groupBy(cm.timeline, (v) => v.month);
    const updatedMonths = Object.entries(months).map(([_, items]) => {
      const endOfMonth = moment(items[0].date).endOf('month').format('YYYY-MM-DD') as TDate;
      return endOfMonth;
    });
    return {
      ...cm,
      endOfMonths: updatedMonths,
    };
  },
  updateWeekends: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    const start = cm.timeline[0];
    const end = cm.timeline[cm.timeline.length - 1];
    let currentDate = moment(start.date);
    const weekends: TDate[] = [];
    while (currentDate) {
      if (currentDate.isAfter(moment(end.date))) {
        break;
      }
      const dayOfWeek = currentDate.weekday();
      const isWeekend = dayOfWeek === 6 || dayOfWeek === 0; // 6 = Saturday, 0 = Sunday
      if (isWeekend) {
        weekends.push(currentDate.format('YYYY-MM-DD') as TDate);
      }
      currentDate = currentDate.add(1, 'd');
    }

    return {
      ...cm,
      weekends,
    };
  },
  updateTimeRange: <T1, T2, T3>(cm: TTimelineModel<T1, T2, T3>): TTimelineModel<T1, T2, T3> => {
    const range = cm.window;
    if (!range) return cm;
    const [startWindow, endWindow] = range;
    const [startTimeline, endTimeline] = [cm.timeline[0].date, cm.timeline[cm.timeline.length - 1].date];
    return {
      ...cm,
      disable: {
        prev: moment(startWindow).isAfter(moment(startTimeline)),
        next: moment(endWindow).isBefore(moment(endTimeline)),
      },
    };
  },
};
